// Parts Search — storefront SEARCH BOX enhancer. Finds the host storefront's
// existing search input and turns it into a Parts Finder autocomplete: it clones
// the input to strip the site's own handlers, then drives a mega-menu suggestion
// dropdown fed by a hidden same-origin iframe (parts-search-iframe.html) over a
// postMessage protocol.
//
// GLOBAL widget: there is NO dedicated container element — it enhances whatever
// search input it can locate via a list of common selectors. init() no-ops
// cleanly on any page where no search input is found.
//
// COMPLEX widget: the iframe/postMessage suggestion protocol, the input-clone
// plugin-hijack cleanup, the vehicle-filter detection/merge, and the role-based
// price/stock gating are the widget's own specialised machinery and are preserved
// verbatim. Only the generic helpers were swapped for the shared bundle:
//   - app base URL      -> getAppBaseUrl()   (was derived from document.currentScript)
//   - apiKey resolver    -> getApiKey() via apiJson (was ?apiKey= on the script src)
//   - features cache      -> loadFeatures()  (was a private _loadFeaturesOnce)
//   - generic API fetches -> apiJson()        (auto-attaches Bearer + base URL)
//
// NOTE: pairs with app/public/js/parts-search.js (the iframe-side script served as
// parts-search-iframe.html). Do NOT confuse the two — the message contract
// (GET_SUGGESTIONS / SUGGESTIONS_RESPONSE / SUGGESTIONS_ERROR) must stay in sync.

import { getAppBaseUrl } from '../shared/config.js';
import { apiJson } from '../shared/api.js';
import { loadFeatures, readCookie, getSessionRef, isAnonymous } from '../shared/identity.js';
import { formatPriceIncl, formatPriceExcl } from '../shared/format.js';
import { isPresentationMode } from '../shared/presentation.js';

// Opt-in debug logging: silent by default, enable with ?pfdebug=1 or
// localStorage pfdebug=1. Genuine problems still use console.warn/error.
const __pfDbg = (() => {
  try {
    if (/[?&]pfdebug=1/.test(location.search)) return true;
    if (localStorage.getItem('pfdebug') === '1') return true;
  } catch (e) { /* storage blocked → stay silent */ }
  return false;
})() ? console.log.bind(console) : function () {};

var _appBaseUrl = '';
var _parentSiteBaseUrl = '';
var _iframe = null;
var _searchInput = null;
var _suggestionsContainer = null;
var _currentQuery = '';
var _debounceTimer = null;
var _selectedIndex = -1;
var _suggestions = [];
var _isLoadingSuggestions = false;
// Enter-before-suggestions race guard: the query the current `_suggestions` are
// for, the last query we asked the iframe for, and (when set) a query the user
// pressed Enter on before its suggestions were ready — applied the moment they
// land (see _submitSearch + the SUGGESTIONS_RESPONSE handler). Without this a fast
// Enter navigated with a plain query and ignored a detected vehicle (e.g. "audi"
// → 0 parts instead of selecting the Audi make).
var _suggestionsQuery = '';
var _lastRequestedQuery = '';
var _pendingEnterQuery = null;
var _lastSubmitAt = 0;

// ── Branch-aware stock for the suggestion pill ────────────────────────────
// The suggest API returns the Solr AGGREGATE stock_status, which ignores the
// shopper's selected branch and so disagrees with the branch-aware results /
// detail pages. Resolve it the SAME way those do: the selected branch's on-hand
// + Head Office (hidden warehouses) from the suggestion's `warehouse_stock`
// map. Falls back to the aggregate when there's no selected branch / per-branch
// data. Mirrors product-price.js's _branchAwareStock.
var _PS_HIDDEN_BRANCHES = ['headoffice', 'hq', 'capedc'];
var _psBranchList = null;

function _psNormBranch(s) { return String(s || '').toLowerCase().replace(/[\s_-]/g, ''); }

function _psSelectedBranchName() {
	try { return ((window.top || window).localStorage.getItem('SelectedBranchName') || '').trim(); }
	catch (e) { try { return (localStorage.getItem('SelectedBranchName') || '').trim(); } catch (e2) { return ''; } }
}

// Warm the branch list (code → name, head-office identification) so it's ready
// by the time suggestions render. Best-effort; branch-aware resolution just
// falls back to the aggregate until it lands.
function _psWarmBranchList() {
	if (_psBranchList) return;
	try {
		apiJson('/api/branches', null, null)
			.then(function (data) { if (Array.isArray(data) && data.length) _psBranchList = data; })
			.catch(function () { /* non-fatal */ });
	} catch (e) { /* ignore */ }
}

function _psBranchAwareStatus(ws) {
	var sel = _psNormBranch(_psSelectedBranchName());
	if (!sel || !ws || typeof ws !== 'object') return null;
	var nameByCode = {};
	(_psBranchList || []).forEach(function (b) {
		var sc = (b.branchShortCode || b.branchCode || '').trim().toUpperCase();
		var nm = (b.branchName || b.name || '').trim();
		if (sc) nameByCode[sc] = nm;
	});
	function dispName(key) { return nameByCode[key.toUpperCase()] || key; }
	function isHidden(key) {
		return _PS_HIDDEN_BRANCHES.indexOf(_psNormBranch(key)) !== -1 || _PS_HIDDEN_BRANCHES.indexOf(_psNormBranch(dispName(key))) !== -1;
	}
	var selQty = 0, hiddenQty = 0;
	Object.keys(ws).forEach(function (key) {
		var q = ws[key] || 0;
		if (isHidden(key)) { hiddenQty += q; return; }
		if (_psNormBranch(key) === sel || _psNormBranch(dispName(key)) === sel) selQty += q;
	});
	return (selQty + hiddenQty) > 0 ? 'in_stock' : 'out_of_stock';
}

// The status to show on a suggestion's stock pill — branch-aware when possible,
// else the aggregate the suggest API returned.
function _psResolveStock(suggestion) {
	return _psBranchAwareStatus(suggestion.warehouse_stock) || suggestion.stockStatus;
}

// ── Customer-specific price for the suggestion rows ───────────────────────
// The suggest API returns the Solr (Standard / retail) price. Signed-in B2B /
// workshop / branch users must see THEIR price, the same one the product cards
// resolve, so after each suggestions render we batch-request
// /api/prices/customer for the visible part SKUs. Same request + auth as
// parts-slider.js / product-price.js: a raw fetch carrying the storefront
// Session cookie (X-Session-Reference) + commerce_features JWT
// (X-Commerce-Features); the server resolves the role pricelist from those.
//
// Results are cached per SKU for the page session, keyed to the visitor's
// identity (cache drops if the Session / JWT cookie changes). A SKU the server
// returns no override for is cached as 0 = "use the Solr price". Failures are
// NOT cached; the row falls back to the Solr price.
var _psPriceCache = {};
var _psPriceIdentity = null;
var _psPriceAbort = null;
var _psPriceRenderSeq = 0;

function _psIdentityKey() {
	return getSessionRef() + '|' + readCookie('commerce_features');
}

function _psPriceCacheForIdentity() {
	var key = _psIdentityKey();
	if (key !== _psPriceIdentity) {
		_psPriceIdentity = key;
		_psPriceCache = {};
	}
	return _psPriceCache;
}

// commerce_features `acc` claim > 0 = a customer account is signed in (same
// client-side read as credit-guard.js). Used only while the role is unresolved.
function _psJwtSignedIn() {
	var jwt = readCookie('commerce_features');
	if (!jwt) return false;
	try {
		var parts = jwt.split('.');
		if (parts.length < 2) return false;
		var b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		while (b64.length % 4) b64 += '=';
		var claims = JSON.parse(atob(b64));
		return Number(claims && claims.acc) > 0;
	} catch (e) { return false; }
}

// Should the dropdown resolve customer prices at all? Anonymous (Public) gets
// the Solr price as before (the server would return nothing anyway), and in
// Presentation Mode the cards show RETAIL, which is the Solr price.
function _psNeedsCustomerPrice() {
	if (isPresentationMode()) return false;
	if (!getSessionRef() && !readCookie('commerce_features')) return false;
	var roleKnown = false;
	try { roleKnown = window.__PARTS_FINDER_FEATURES_ROLE_KEY__ !== undefined; } catch (e) { /* ignore */ }
	return roleKnown ? !isAnonymous() : _psJwtSignedIn();
}

// "R465.00 incl. VAT" (or excl, per the role's VAT toggles), formatted with
// the same shared helpers as the product cards. null = no price to show.
function _psPriceText(excl) {
	var f = (typeof window !== 'undefined' && window.__PARTS_FINDER_FEATURES__) || {};
	if (f.showPriceInclVat !== false) {
		var incl = formatPriceIncl(excl);
		return incl ? incl + ' incl. VAT' : null;
	}
	if (f.showPriceExclVat !== false) {
		var ex = formatPriceExcl(excl);
		return ex ? ex + ' excl. VAT' : null;
	}
	return null;
}

var _PS_PRICE_STYLE = 'font-size: 13px; font-weight: 700; color: #d9272e; margin-left: auto;';
var _PS_PRICE_PENDING_STYLE = 'display: inline-block; margin-left: auto; width: 88px; height: 12px; border-radius: 6px; background: linear-gradient(90deg, #eef0f2 25%, #e2e5e8 50%, #eef0f2 75%); background-size: 200% 100%; animation: pf-ps-shimmer 1.2s ease-in-out infinite;';

function _psEnsureShimmerCss() {
	if (document.getElementById('pf-ps-shimmer-css')) return;
	var st = document.createElement('style');
	st.id = 'pf-ps-shimmer-css';
	st.textContent = '@keyframes pf-ps-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }';
	(document.head || document.documentElement).appendChild(st);
}

// Fill a row's price slot. `customerExcl` undefined = use the Solr price.
// Removes the slot when there is nothing to show (zero / no price).
function _psFillPrice(el, suggestion, customerExcl) {
	if (!el) return;
	var solrText = _psPriceText(suggestion.price);
	var text = (typeof customerExcl === 'number' && customerExcl > 0) ? _psPriceText(customerExcl) : solrText;
	if (!text) { if (el.parentNode) el.parentNode.removeChild(el); return; }
	el.removeAttribute('aria-busy');
	el.removeAttribute('aria-label');
	el.style.cssText = _PS_PRICE_STYLE;
	el.textContent = text;
	// Presentation Mode toggled while the dropdown is open swaps to retail
	// (the Solr price) via shared/presentation.js, like the cards.
	if (solrText && solrText !== text) {
		el.setAttribute('data-pf-price-trade', text);
		el.setAttribute('data-pf-price-retail', solrText);
	}
}

// Build the price slot for a part row: resolved price, or a shimmer
// placeholder (never the retail price first) while the customer price loads.
function _psCreatePriceEl(suggestion) {
	var el = document.createElement('span');
	el.className = 'pf-ps-price';
	var sku = suggestion.sku || '';
	if (sku && _psNeedsCustomerPrice()) {
		var cache = _psPriceCacheForIdentity();
		if (Object.prototype.hasOwnProperty.call(cache, sku)) {
			_psFillPrice(el, suggestion, cache[sku]);
			return el.textContent ? el : null;
		}
		_psEnsureShimmerCss();
		el.setAttribute('data-pf-sku', sku);
		el.setAttribute('aria-busy', 'true');
		el.setAttribute('aria-label', 'Loading price');
		el.style.cssText = _PS_PRICE_PENDING_STYLE;
		return el;
	}
	_psFillPrice(el, suggestion, undefined);
	return el.textContent ? el : null;
}

// After a render: one batched request for the visible part SKUs that are not
// cached yet. Cancels the previous in-flight request (stale query).
function _psResolveCustomerPrices(partSuggestions) {
	var seq = ++_psPriceRenderSeq;
	if (_psPriceAbort) { try { _psPriceAbort.abort(); } catch (e) { /* ignore */ } _psPriceAbort = null; }
	if (!_suggestionsContainer) return;
	var pending = _suggestionsContainer.querySelectorAll('.pf-ps-price[data-pf-sku]');
	if (!pending.length) return;

	var bySku = {};
	for (var i = 0; i < partSuggestions.length; i++) {
		var s = partSuggestions[i];
		if (s && s.sku && !bySku[s.sku]) bySku[s.sku] = s;
	}
	var skus = [];
	for (var j = 0; j < pending.length; j++) {
		var sk = pending[j].getAttribute('data-pf-sku');
		if (sk && skus.indexOf(sk) === -1) skus.push(sk);
	}

	function settle(cacheOrNull) {
		if (seq !== _psPriceRenderSeq || !_suggestionsContainer) return;
		var nodes = _suggestionsContainer.querySelectorAll('.pf-ps-price[data-pf-sku]');
		for (var k = 0; k < nodes.length; k++) {
			var n = nodes[k];
			var key = n.getAttribute('data-pf-sku');
			n.removeAttribute('data-pf-sku');
			var sug = bySku[key] || { price: 0 };
			_psFillPrice(n, sug, cacheOrNull ? cacheOrNull[key] : undefined);
		}
	}

	var identity = _psIdentityKey();
	var headers = { 'Accept': 'application/json' };
	var sessionRef = getSessionRef();
	var jwt = readCookie('commerce_features');
	if (sessionRef) headers['X-Session-Reference'] = sessionRef;
	if (jwt) headers['X-Commerce-Features'] = jwt;
	var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
	_psPriceAbort = ctrl;
	var base = getAppBaseUrl().replace(/\/$/, '');
	fetch(base + '/api/prices/customer?skus=' + encodeURIComponent(skus.join(',')), {
		method: 'GET', headers: headers, cache: 'no-store', signal: ctrl ? ctrl.signal : undefined,
	})
		.then(function (r) { if (!r.ok) throw new Error('prices ' + r.status); return r.json(); })
		.then(function (data) {
			if (_psPriceAbort === ctrl) _psPriceAbort = null;
			if (identity !== _psIdentityKey()) { settle(null); return; }
			var cache = _psPriceCacheForIdentity();
			for (var a = 0; a < skus.length; a++) cache[skus[a]] = 0; // default: no override
			var list = (data && data.prices) ? data.prices : [];
			for (var b = 0; b < list.length; b++) {
				var p = list[b];
				if (p && p.sku && typeof p.price === 'number' && p.price > 0) cache[p.sku] = p.price;
			}
			settle(cache);
		})
		.catch(function (err) {
			if (_psPriceAbort === ctrl) _psPriceAbort = null;
			if (err && err.name === 'AbortError') return; // superseded by a newer render
			settle(null); // fail open: Solr price
		});
}

function _initializeSearch() {
	// Use jQuery if the host storefront exposes it, otherwise vanilla JS.
	var $ = (typeof window !== 'undefined' && window.jQuery) ? window.jQuery : null;

	// Try to find existing search input
	// Common selectors for search boxes
	var searchSelectors = [
		'input[type="search"]',
		'input[name="search"]',
		'input[name="q"]',
		'input[name="query"]',
		'input[placeholder*="search" i]',
		'input[placeholder*="Search" i]',
		'input[id*="search" i]',
		'input[class*="search" i]',
		'.search-input',
		'#search-input',
		'form[action*="search"] input[type="text"]',
	];

	var searchInput = null;
	if (typeof $ === 'function' && $.fn) {
		for (var i = 0; i < searchSelectors.length; i++) {
			var $el = $(searchSelectors[i]).first();
			if ($el.length > 0 && $el.is(':visible')) {
				searchInput = $el[0];
				break;
			}
		}
	} else {
		// Vanilla JS fallback
		for (var i = 0; i < searchSelectors.length; i++) {
			var el = document.querySelector(searchSelectors[i]);
			if (el && el.offsetParent !== null) { // Check if visible
				searchInput = el;
				break;
			}
		}
	}

	if (!searchInput) {
		console.warn('PartsSearch: Could not find search input on page');
		return;
	}

	// We have a search box to enhance — wire up the iframe message listener and
	// pre-populate the role-feature cache now (deferred until here so pages with
	// no search box do no work). The search box is often the ONLY embed on a page
	// (header search); loading features here means showPrices / showStockStatus
	// gating works even in that scenario, otherwise the cache stays empty and
	// every flag defaults true (least-restrictive).
	window.addEventListener('message', _handleMessageFromIframe);
	loadFeatures();
	// Warm the branch list so the suggestion stock pill can be branch-aware
	// (consistent with the results grid + product detail page).
	_psWarmBranchList();

	// Store reference
	_searchInput = searchInput;

	// Clone the input to remove all existing event listeners
	// This prevents conflicts with the site's existing functionality
	var newInput = searchInput.cloneNode(true);
	newInput.value = searchInput.value;

	// Replace the original input with the clone
	searchInput.parentNode.replaceChild(newInput, searchInput);
	searchInput = newInput;
	_searchInput = searchInput;

	// Create hidden iframe for communication
	_renderSearchIframe();

	// Wrap search input in a container for positioning suggestions
	var $searchInput = typeof $ === 'function' ? $(searchInput) : null;
	var wrapper;
	var formElement = searchInput.closest('form');
	var parentElement = formElement ? formElement.parentNode : searchInput.parentNode;

	if ($searchInput && $searchInput.parent && $searchInput.parent().hasClass('parts-search-wrapper')) {
		wrapper = $searchInput.parent()[0];
	} else {
		// Create wrapper using vanilla JS
		wrapper = document.createElement('div');
		wrapper.className = 'parts-search-wrapper';
		wrapper.style.cssText = 'position: relative; display: inline-block; width: 100%;';
		searchInput.parentNode.insertBefore(wrapper, searchInput);
		wrapper.appendChild(searchInput);
	}

	// Create suggestions dropdown container (mega menu style)
	// Inject it outside and below the form element, not inside it
	_suggestionsContainer = document.createElement('div');
	_suggestionsContainer.className = 'parts-search-suggestions';
	_suggestionsContainer.style.cssText = 'display: none; position: absolute; z-index: 9999; width: 100%; max-width: 1200px; min-width: 600px; background: white; border: 2px solid #ced4da; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); max-height: 600px; overflow-y: auto; margin-top: 4px;';

	// Append to form's parent (outside the form) or wrapper's parent if no form
	if (formElement && parentElement) {
		// Insert after the form element (outside the form)
		if (formElement.nextSibling) {
			parentElement.insertBefore(_suggestionsContainer, formElement.nextSibling);
		} else {
			parentElement.appendChild(_suggestionsContainer);
		}
	} else {
		// Fallback: append to wrapper's parent if wrapper exists
		if (wrapper && wrapper.parentNode) {
			wrapper.parentNode.insertBefore(_suggestionsContainer, wrapper.nextSibling);
		} else {
			// Last resort: append to wrapper
			wrapper.appendChild(_suggestionsContainer);
		}
	}

	// Update positioning to be relative to the wrapper (which contains the input)
	// The container is outside the form but should still position relative to the input
	var updatePosition = function() {
		if (_suggestionsContainer && wrapper) {
			var wrapperRect = wrapper.getBoundingClientRect();
			_suggestionsContainer.style.top = (wrapperRect.bottom + window.scrollY) + 'px';
			_suggestionsContainer.style.left = wrapperRect.left + 'px';
			_suggestionsContainer.style.width = wrapperRect.width + 'px';
		}
	};

	// Update position on scroll and resize
	window.addEventListener('scroll', updatePosition, true);
	window.addEventListener('resize', updatePosition);

	// Store update function for later use
	_suggestionsContainer._updatePosition = updatePosition;

	// Add event listeners (on the cloned input, so no conflicts)
	searchInput.addEventListener('input', _handleInput);
	searchInput.addEventListener('keydown', _handleKeyDown);
	searchInput.addEventListener('focus', _handleFocus);
	searchInput.addEventListener('blur', function(e) {
		// Delay hiding to allow click events on suggestions
		setTimeout(function() {
			var relatedTarget = e.relatedTarget || document.activeElement;
			if (!relatedTarget || !_suggestionsContainer.contains(relatedTarget)) {
				_hideSuggestions();
			}
		}, 200);
	});

	// Prevent form submission on Enter if suggestions are visible
	var form = searchInput.closest ? searchInput.closest('form') : (function() {
		var el = searchInput;
		while (el && el.tagName !== 'FORM') {
			el = el.parentElement;
		}
		return el;
	})();
	if (form) {
		form.addEventListener('submit', function(e) {
			e.preventDefault();
			e.stopPropagation();
			_submitSearch();
		});
	}

	// Enable the search input and search button (they start disabled until the script takes over)
	searchInput.removeAttribute('disabled');
	searchInput.classList.remove('disabled');
	var submitBtn = form ? form.querySelector('button') : null;
	if (submitBtn) {
		submitBtn.removeAttribute('disabled');
		submitBtn.classList.remove('disabled');
		// Handle click on search button (may be type="button" so won't trigger form submit)
		submitBtn.addEventListener('click', function(e) {
			e.preventDefault();
			_submitSearch();
		});
	}

	__pfDbg('PartsSearch: Initialized on search input');
}

function _renderSearchIframe() {
	// Check if embed script iframe already exists (can share the same iframe)
	var existingIframe = document.getElementById('parts-finder-comm-iframe');
	if (existingIframe) {
		__pfDbg('PartsSearch: Reusing existing embed iframe');
		_iframe = existingIframe;
		// Mark that we're also using it
		existingIframe.setAttribute('data-parts-search', '');
		// Also set the ID that search script expects
		if (!existingIframe.id) {
			existingIframe.id = 'parts-search-iframe';
		}
		return;
	}

	// Check if search iframe already exists
	existingIframe = document.getElementById('parts-search-iframe');
	if (existingIframe) {
		__pfDbg('PartsSearch: Reusing existing search iframe');
		_iframe = existingIframe;
		return;
	}

	// Create hidden iframe for communication
	// API key is hardcoded in the iframe script
	var iframeSrc = _appBaseUrl + 'parts-search-iframe.html';

	var iframe = document.createElement('iframe');
	iframe.setAttribute('data-parts-search', '');
	iframe.id = 'parts-search-iframe';
	iframe.height = '1';
	iframe.width = '1';
	iframe.style.cssText = 'border:0; position:absolute; left:-9999px; top:-9999px; visibility:hidden;';
	iframe.scrolling = 'no';
	iframe.src = iframeSrc;

	var body = document.body || document.getElementsByTagName('body')[0];
	body.appendChild(iframe);
	_iframe = iframe;
}

function _handleInput(e) {
	var query = e.target.value;
	_currentQuery = query;
	_selectedIndex = -1;
	// Typing again cancels any Enter that was waiting on an earlier query.
	_pendingEnterQuery = null;

	// Clear previous debounce timer
	if (_debounceTimer) {
		clearTimeout(_debounceTimer);
	}

	// Debounce API calls (250ms)
	_debounceTimer = setTimeout(function() {
		if (query.length >= 2) {
			_requestSuggestions(query);
		} else {
			_hideSuggestions();
		}
	}, 250);
}

function _handleKeyDown(e) {
	// Enter commits the search. Always handle it here (preventing the native form
	// submit) and route through _submitSearch, which waits for in-flight/pending
	// suggestions before deciding — so a fast Enter selects a detected vehicle
	// instead of running a plain, zero-result text search.
	if (e.key === 'Enter') {
		e.preventDefault();
		e.stopPropagation();
		_submitSearch();
		return;
	}

	// Handle other keys only when suggestions are visible
	if (!_suggestions || _suggestions.length === 0) {
		return;
	}

	switch(e.key) {
		case 'ArrowDown':
			e.preventDefault();
			_selectedIndex = Math.min(_selectedIndex + 1, _suggestions.length - 1);
			_updateSelection();
			break;
		case 'ArrowUp':
			e.preventDefault();
			_selectedIndex = Math.max(_selectedIndex - 1, -1);
			_updateSelection();
			break;
		case 'Escape':
			e.preventDefault();
			_hideSuggestions();
			break;
	}
}

function _updateSelection() {
	// Remove all selected classes
	var allItems = _suggestionsContainer.querySelectorAll('.suggestion-item');
	allItems.forEach(function(item) {
		item.classList.remove('selected');
		// Reset styles
		if (item.style) {
			item.style.backgroundColor = '';
			item.style.borderColor = '';
			item.style.boxShadow = '';
		}
	});

	// Highlight selected item
	if (_selectedIndex >= 0 && _selectedIndex < allItems.length) {
		var selected = allItems[_selectedIndex];
		selected.classList.add('selected');
		selected.style.backgroundColor = '#f0f7ff';
		selected.style.borderColor = '#d9272e';
		selected.style.boxShadow = '0 2px 8px rgba(230, 126, 34, 0.3)';

		// Scroll into view
		selected.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
	}
}

function _handleFocus(e) {
	if (_currentQuery.length >= 2 && _suggestions.length > 0) {
		_showSuggestions();
	}
}

function _requestSuggestions(query) {
	if (!_iframe || !_iframe.contentWindow) {
		console.warn('PartsSearch: Iframe not ready');
		return;
	}

	_isLoadingSuggestions = true;
	_lastRequestedQuery = query;
	// Show the pulsing-G loading indicator immediately so the dropdown has a
	// stable, in-place loading state (no layout jump) while we wait for the
	// iframe to return suggestions.
	_renderLoading();
	_sendMessageToIframe({
		type: 'GET_SUGGESTIONS',
		data: {
			query: query
		}
	});
}

// Render the "pulsing G" loading state inside the dropdown — the Goldwagen
// logo mark gently pulsing, matching the Parts Finder loading animation.
// Replaces the old (no) loading feedback and keeps the container height
// stable so results don't jump the page when they land.
function _renderLoading() {
	if (!_suggestionsContainer) return;
	_suggestionsContainer.innerHTML =
		'<div style="padding: 44px 20px; text-align: center;">' +
			'<img src="' + _appBaseUrl + 'logo-icon.png" alt="Searching" ' +
				'style="width: 72px; height: auto; display: inline-block; animation: pf-pulse 1.2s ease-in-out infinite;" />' +
			'<div style="margin-top: 14px; font-size: 13px; font-weight: 600; color: #6c757d; letter-spacing: 0.3px;">Searching parts…</div>' +
		'</div>';
	_showSuggestions();
}


// Single entry point for "the user committed the search" (Enter, form submit, or
// the search button). Highlighted suggestion → open it. Otherwise, if the
// suggestions for the CURRENT query aren't ready yet (debounce still pending, a
// request in flight, or the loaded set is for an older query), stash the query and
// apply it the instant the suggestions land — flushing the debounce so we don't
// wait out the full 250ms. Only when ready do we navigate, so a detected vehicle
// is honoured instead of falling back to a plain (zero-result) text search.
function _submitSearch() {
	var now = Date.now();
	if (now - _lastSubmitAt < 400) return; // de-dupe Enter+submit / click+submit for one action
	_lastSubmitAt = now;

	if (_selectedIndex >= 0 && _selectedIndex < _suggestions.length) {
		_selectSuggestion(_suggestions[_selectedIndex]);
		return;
	}
	var q = (_currentQuery || '').trim();
	var ready = q.length < 2 || (!_isLoadingSuggestions && _suggestionsQuery === q);
	if (!ready) {
		_pendingEnterQuery = q;
		if (_debounceTimer) { clearTimeout(_debounceTimer); _debounceTimer = null; }
		_requestSuggestions(q);
		return;
	}
	_navigateWithDetectedFilters(_currentQuery);
}

function _navigateToPartsFinder(query) {
	if (!query || query.trim() === '') {
		return;
	}

	// Hide suggestions
	_hideSuggestions();

	// Navigate to parent site's /parts-finder page with query
	var partsFinderUrl = _parentSiteBaseUrl + 'parts-finder';
	window.location.href = partsFinderUrl + '?q=' + encodeURIComponent(query.trim());
}

/**
 * Merge detectedFilters from all current suggestions into a single object.
 * Returns null if no suggestion has detectedFilters.
 */
function _getMergedDetectedFilters() {
	var merged = null;
	for (var i = 0; i < _suggestions.length; i++) {
		var s = _suggestions[i];
		if (!s.detectedFilters) continue;
		if (!merged) {
			merged = {
				make: s.detectedFilters.make || null,
				model: s.detectedFilters.model || null,
				year: s.detectedFilters.year || null,
				engine: s.detectedFilters.engine || null
			};
		} else {
			merged.make = s.detectedFilters.make || merged.make;
			merged.model = s.detectedFilters.model || merged.model;
			merged.year = s.detectedFilters.year || merged.year;
			merged.engine = s.detectedFilters.engine || merged.engine;
		}
	}
	return merged;
}

/**
 * Fetch the first available engine/variant for a given make+model from the API.
 * Returns null if unavailable or on error. apiJson attaches the Bearer key and
 * base URL and resolves to null on any non-OK / parse error.
 */
async function _fetchFirstEngine(make, model) {
	// Fetch just this make's detail (~6KB) instead of the full vehicle tree.
	var data = await apiJson('/api/filters/vehicle/make/' + encodeURIComponent(make), null, null);
	var engines = data && data.engines && data.engines[model];
	return (Array.isArray(engines) && engines.length > 0) ? engines[0] : null;
}

/**
 * Navigate to parts-finder with the current query, applying any detected vehicle
 * filters from suggestions. Auto-selects the first engine/variant when make+model
 * are detected but no engine was specified. Falls back to a plain query search if
 * no vehicle filters are detected.
 */
async function _navigateWithDetectedFilters(query) {
	var mergedFilters = _getMergedDetectedFilters();
	if (mergedFilters && (mergedFilters.make || mergedFilters.model)) {
		// Auto-select first engine/variant if not already detected
		if (!mergedFilters.engine && mergedFilters.make && mergedFilters.model) {
			var firstEngine = await _fetchFirstEngine(mergedFilters.make, mergedFilters.model);
			if (firstEngine) {
				mergedFilters.engine = firstEngine;
			}
		}
		// Apply vehicle filters — same logic as clicking a vehicle suggestion
		_selectSuggestion({ display: query, type: 'model', detectedFilters: mergedFilters });
	} else {
		_navigateToPartsFinder(query);
	}
}

/**
 * Resolve storefront product URL via Parts Finder API (same logic as View Details in slider),
 * then redirect. Fallback to parts-finder part page if API fails or returns no URL. apiJson
 * attaches the Bearer key + base URL and resolves to null on any non-OK / parse error.
 */
function _navigateToPartDetailUrl(partId) {
	if (!partId) return;
	apiJson('/api/parts/' + encodeURIComponent(partId) + '/storefront-url', null, null)
		.then(function (data) {
			var url = (data && data.url) ? data.url.trim() : '';
			if (url) {
				window.location.href = url;
			} else {
				console.warn('PartsSearch: Commerce SDK returned no URL for', partId);
			}
		});
}

function _selectSuggestion(suggestion) {
	if (!suggestion) return;

	// Hide suggestions
	_hideSuggestions();

	// NB: do NOT write suggestion.display back into the input here. We navigate
	// away on every branch below, so setting the value only causes a visible flash
	// of the full code (e.g. the full supplier number) before the page changes —
	// client feedback (arrow-down + Enter flashed the full supplier number).

	// Navigate to parent site's /parts-finder page (or SDK product URL for parts)
	var partsFinderUrl = _parentSiteBaseUrl + 'parts-finder';
	var params = new URLSearchParams();
	var partId = suggestion.partId || suggestion.sku;

	if (partId) {
		// Resolve product URL from SDK (same as View Details) then redirect
		_navigateToPartDetailUrl(partId);
		return;
	}
	if (suggestion.type === 'department') {
		// Navigate to search with department filter
		params.set('q', suggestion.display);
		if (suggestion.departmentCode) {
			params.set('department', suggestion.departmentCode);
		}
		window.location.href = partsFinderUrl + '?' + params.toString();
	} else if (suggestion.type === 'category') {
		// Navigate to search with category filter
		params.set('q', suggestion.display);
		if (suggestion.category) {
			params.set('category', suggestion.category);
		}
		window.location.href = partsFinderUrl + '?' + params.toString();
	} else if (suggestion.detectedFilters) {
		// Navigate to search page with vehicle filters only — do NOT include q,
		// since the display text is a vehicle description not a part name.
		if (suggestion.detectedFilters.make) params.set('make', suggestion.detectedFilters.make);
		if (suggestion.detectedFilters.model) params.set('model', suggestion.detectedFilters.model);
		if (suggestion.detectedFilters.year) params.set('year', suggestion.detectedFilters.year);
		if (suggestion.detectedFilters.engine) params.set('engine', suggestion.detectedFilters.engine);
		window.location.href = partsFinderUrl + '?' + params.toString();
	} else {
		// Navigate to search page with query
		window.location.href = partsFinderUrl + '?q=' + encodeURIComponent(suggestion.display);
	}
}

function _showSuggestions() {
	if (_suggestionsContainer) {
		// Update position before showing
		if (_suggestionsContainer._updatePosition) {
			_suggestionsContainer._updatePosition();
		}
		_suggestionsContainer.style.display = 'block';
	}
}

function _hideSuggestions() {
	if (_suggestionsContainer) {
		_suggestionsContainer.style.display = 'none';
	}
	_selectedIndex = -1;
}

function _renderSuggestions(suggestions) {
	if (!_suggestionsContainer) return;

	_suggestions = suggestions || [];

	__pfDbg('PartsSearch: Rendering mega menu suggestions', _suggestions.length, 'items');

	if (_suggestions.length === 0) {
		_hideSuggestions();
		return;
	}

	// Clear existing suggestions
	_suggestionsContainer.innerHTML = '';

	// Organize suggestions by type
	var parts = [];
	var departments = [];
	var categories = [];
	var vehicles = [];
	var codes = []; // SKU/OEM (plain text fallback)
	var seenPartIds = {};
	var aggregatedFilters = {
		make: null,
		model: null,
		year: null,
		engine: null
	};

	_suggestions.forEach(function(suggestion) {
		// Aggregate vehicle filters
		if (suggestion.detectedFilters) {
			if (suggestion.detectedFilters.make && !aggregatedFilters.make) {
				aggregatedFilters.make = suggestion.detectedFilters.make;
			}
			if (suggestion.detectedFilters.model && !aggregatedFilters.model) {
				aggregatedFilters.model = suggestion.detectedFilters.model;
			}
			if (suggestion.detectedFilters.year && !aggregatedFilters.year) {
				aggregatedFilters.year = suggestion.detectedFilters.year;
			}
			if (suggestion.detectedFilters.engine && !aggregatedFilters.engine) {
				aggregatedFilters.engine = suggestion.detectedFilters.engine;
			}
		}

		// Categorize suggestions
		if (suggestion.type === 'department') {
			departments.push(suggestion);
		} else if (suggestion.type === 'category') {
			categories.push(suggestion);
		} else if (suggestion.type === 'make' || suggestion.type === 'model' || suggestion.type === 'year' || suggestion.type === 'engine') {
			vehicles.push(suggestion);
		} else if (suggestion.type === 'sku' || suggestion.type === 'oem') {
			// Route to rich card column if there is ANY meaningful detail beyond just the code
			var isRich = suggestion.title || suggestion.imageUrl || suggestion.brand || suggestion.category;
			if (isRich) {
				var pid = suggestion.partId || suggestion.sku;
				if (pid && seenPartIds[pid]) return; // deduplicate
				if (pid) seenPartIds[pid] = true;
				parts.push(suggestion);
			} else {
				codes.push(suggestion);
			}
		} else if (suggestion.partId || suggestion.type === 'part') {
			var pid2 = suggestion.partId || suggestion.sku;
			if (pid2 && seenPartIds[pid2]) return; // deduplicate
			if (pid2) seenPartIds[pid2] = true;
			parts.push(suggestion);
		}
	});

	var hasAnyFilter = aggregatedFilters.make || aggregatedFilters.model || aggregatedFilters.year || aggregatedFilters.engine;

	// Vehicle filter header
	if (hasAnyFilter) {
		var filterHeader = document.createElement('div');
		filterHeader.style.cssText = 'padding: 14px 20px; background: linear-gradient(135deg, rgba(230, 126, 34, 0.15), rgba(30, 58, 95, 0.15)); border-bottom: 2px solid rgba(230, 126, 34, 0.4);';

		var filterLabel = document.createElement('div');
		filterLabel.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 13px; font-weight: 700; color: #1e3a5f; text-transform: uppercase; letter-spacing: 0.5px;';
		filterLabel.innerHTML = '<svg style="width: 16px; height: 16px; color: #d9272e;" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg><span>Vehicle Detected</span>';
		filterHeader.appendChild(filterLabel);

		var filterBadges = document.createElement('div');
		filterBadges.style.cssText = 'display: flex; flex-wrap: wrap; gap: 8px;';

		if (aggregatedFilters.make) {
			var makeBadge = document.createElement('span');
			makeBadge.textContent = aggregatedFilters.make;
			makeBadge.style.cssText = 'font-size: 13px; font-weight: 700; color: #ffffff; background: #1e3a5f; padding: 8px 14px; border-radius: 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.15);';
			filterBadges.appendChild(makeBadge);
		}
		if (aggregatedFilters.model) {
			var modelBadge = document.createElement('span');
			modelBadge.textContent = aggregatedFilters.model;
			modelBadge.style.cssText = 'font-size: 13px; font-weight: 700; color: #ffffff; background: #495057; padding: 8px 14px; border-radius: 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.15);';
			filterBadges.appendChild(modelBadge);
		}
		if (aggregatedFilters.year) {
			var yearBadge = document.createElement('span');
			yearBadge.textContent = aggregatedFilters.year;
			yearBadge.style.cssText = 'font-size: 13px; font-weight: 700; color: #ffffff; background: #d9272e; padding: 8px 14px; border-radius: 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.15);';
			filterBadges.appendChild(yearBadge);
		}
		if (aggregatedFilters.engine) {
			var engineBadge = document.createElement('span');
			engineBadge.textContent = aggregatedFilters.engine;
			engineBadge.style.cssText = 'font-size: 13px; font-weight: 700; color: #ffffff; background: #2c3e50; padding: 8px 14px; border-radius: 20px; box-shadow: 0 2px 6px rgba(0,0,0,0.15);';
			filterBadges.appendChild(engineBadge);
		}

		filterHeader.appendChild(filterBadges);
		_suggestionsContainer.appendChild(filterHeader);
	}

	// Create mega menu container with grid layout
	var megaMenu = document.createElement('div');
	megaMenu.className = 'pf-search-megamenu';
	megaMenu.style.cssText = 'display: grid; grid-template-columns: 1.5fr 1fr; gap: 0;';

	// Left column: Parts
	var leftCol = document.createElement('div');
	leftCol.className = 'pf-search-col-left';
	leftCol.style.cssText = 'padding: 16px; border-right: 1px solid #e9ecef;';

	if (parts.length > 0) {
		var partsHeader = document.createElement('div');
		partsHeader.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #d9272e;';
		partsHeader.innerHTML = '<svg style="width: 18px; height: 18px; color: #d9272e;" fill="currentColor" viewBox="0 0 20 20"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" /></svg><span style="font-weight: 700; font-size: 14px; color: #1e3a5f; text-transform: uppercase; letter-spacing: 0.5px;">Parts</span>';
		leftCol.appendChild(partsHeader);

		var partsList = document.createElement('div');
		partsList.style.cssText = 'display: flex; flex-direction: column; gap: 8px;';

		parts.slice(0, 6).forEach(function(suggestion, index) {
			var item = _createPartItem(suggestion, index);
			partsList.appendChild(item);
		});

		leftCol.appendChild(partsList);
	}

	// Right column: Departments, Categories, Codes, Vehicles
	var rightCol = document.createElement('div');
	rightCol.style.cssText = 'padding: 16px; display: flex; flex-direction: column; gap: 16px;';

	// Departments section
	if (departments.length > 0) {
		var deptSection = _createSection('Departments', departments.slice(0, 4), 'department');
		rightCol.appendChild(deptSection);
	}

	// Categories section
	if (categories.length > 0) {
		var catSection = _createSection('Categories', categories.slice(0, 4), 'category');
		rightCol.appendChild(catSection);
	}

	// Codes section (SKU/OEM) — deduplicate by display value before rendering
	if (codes.length > 0) {
		var seenCodeDisplays = {};
		var uniqueCodes = codes.filter(function(s) {
			var key = (s.sku || s.display || '').toUpperCase();
			if (seenCodeDisplays[key]) return false;
			seenCodeDisplays[key] = true;
			return true;
		});
		if (uniqueCodes.length > 0) {
			var codesSection = _createSection('Codes', uniqueCodes.slice(0, 4), 'code');
			rightCol.appendChild(codesSection);
		}
	}

	// Vehicles section
	if (vehicles.length > 0) {
		var vehiclesSection = _createSection('Vehicles', vehicles, 'vehicle');
		rightCol.appendChild(vehiclesSection);
	}

	// The right column only populates when the query matches a department, category,
	// code or vehicle — for a plain part search it stays empty, which looked broken
	// next to the parts list (client feedback). Show a short hint so the blank space
	// reads as intentional.
	if (rightCol.children.length === 0 && parts.length > 0) {
		var rHint = document.createElement('div');
		rHint.style.cssText = 'padding: 8px 4px; color: #adb5bd; font-size: 12px; line-height: 1.5;';
		rHint.innerHTML = '<div style="font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#6c757d;margin-bottom:6px;">Vehicles & Departments</div>'
			+ 'Matching vehicles, departments and codes show up here. Add a make or model to your search to see them.';
		rightCol.appendChild(rHint);
	}

	// If no parts, make right column full width
	if (parts.length === 0) {
		leftCol.style.display = 'none';
		rightCol.style.gridColumn = '1 / -1';
	}

	megaMenu.appendChild(leftCol);
	megaMenu.appendChild(rightCol);
	_suggestionsContainer.appendChild(megaMenu);
	_showSuggestions();
	// Batch-resolve customer prices for the rendered (uncached) part rows.
	_psResolveCustomerPrices(parts.slice(0, 6));
}

function _createPartItem(suggestion, index) {
	var item = document.createElement('div');
	item.className = 'suggestion-item';
	item.style.cssText = 'display: flex; gap: 12px; padding: 12px; cursor: pointer; border: 1px solid #e9ecef; border-radius: 8px; transition: all 0.2s; background: #ffffff;';
	item.setAttribute('data-index', index);

	// Image
	if (suggestion.imageUrl) {
		var img = document.createElement('img');
		img.src = suggestion.imageUrl;
		img.style.cssText = 'width: 60px; height: 60px; object-fit: contain; border-radius: 6px; background: #f8f9fa; flex-shrink: 0;';
		img.onerror = function() { this.style.display = 'none'; };
		item.appendChild(img);
	} else {
		var placeholder = document.createElement('div');
		placeholder.style.cssText = 'width: 60px; height: 60px; background: #f8f9fa; border-radius: 6px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;';
		placeholder.innerHTML = '<svg style="width: 24px; height: 24px; color: #adb5bd;" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" /></svg>';
		item.appendChild(placeholder);
	}

	// Content
	var content = document.createElement('div');
	content.style.cssText = 'flex: 1; min-width: 0;';

	// For SKU/OEM suggestions routed here, prefer the actual part title over the code
	var mainTitle = suggestion.title || suggestion.display || '';
	var codeSubtitle = (suggestion.type === 'sku' || suggestion.type === 'oem') && suggestion.title ? suggestion.display : null;

	var title = document.createElement('div');
	title.textContent = mainTitle;
	title.style.cssText = 'font-weight: 600; font-size: 14px; color: #212529; margin-bottom: 4px; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;';
	content.appendChild(title);

	if (codeSubtitle) {
		var codeBadge = document.createElement('div');
		var codeLabel = suggestion.type === 'oem' ? 'OEM Code: ' : 'Item Code: ';
		codeBadge.style.cssText = 'font-size: 0.75rem; font-weight: 600; color: #001689; background: #e8ebf7; padding: 2px 7px; border-radius: 4px; display: inline-block; margin-bottom: 4px; letter-spacing: 0.3px;';
		var labelSpan = document.createElement('span');
		labelSpan.textContent = codeLabel;
		labelSpan.style.cssText = 'color: #6c757d; font-weight: 500;';
		codeBadge.appendChild(labelSpan);
		codeBadge.appendChild(document.createTextNode(codeSubtitle));
		content.appendChild(codeBadge);
	}

	var meta = document.createElement('div');
	meta.style.cssText = 'display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 6px;';

	if (suggestion.brand) {
		var brand = document.createElement('span');
		brand.textContent = suggestion.brand;
		brand.style.cssText = 'font-size: 0.875rem; color: #6c757d; font-weight: 500;';
		meta.appendChild(brand);
	}

	if (suggestion.category) {
		var cat = document.createElement('span');
		cat.textContent = suggestion.category;
		cat.style.cssText = 'font-size: 0.75rem; color: #1e3a5f; background: #e9ecef; padding: 2px 8px; border-radius: 12px;';
		meta.appendChild(cat);
	}

	// Role-based feature flags — read synchronously from the shared cache
	// populated by sibling widgets (alt-products / counter-parts / etc.) or by
	// loadFeatures() at init. On the very first search of a page-load the cache
	// may be empty, in which case every flag defaults to true (least-restrictive).
	// Subsequent renders pick up the real flags as soon as the cache lands.
	var __pff = (typeof window !== 'undefined' && window.__PARTS_FINDER_FEATURES__) || {};

	// Suggest returns the Solr (Standard / retail) price EXCL. VAT. Show the
	// visitor's OWN price instead (customer price for signed-in B2B / workshop /
	// branch users, resolved after render by _psResolveCustomerPrices; a shimmer
	// holds the slot meanwhile), formatted like the cards (shared format.js
	// helpers) and honouring the incl/excl VAT flags. One line only: no retail
	// comparison in the dropdown.
	if (__pff.showPrices !== false) {
		var price = _psCreatePriceEl(suggestion);
		if (price) meta.appendChild(price);
	}

	if (suggestion.stockStatus && __pff.showStockStatus !== false) {
		// Branch-aware (selected branch + Head Office), consistent with the
		// results grid / detail page — not the Solr aggregate.
		var stockStatus = _psResolveStock(suggestion);
		var stock = document.createElement('span');
		var stockColor = stockStatus === 'in_stock' ? '#28a745' : stockStatus === 'backorder' ? '#ffc107' : '#dc3545';
		stock.textContent = stockStatus === 'in_stock' ? 'In Stock' : stockStatus === 'backorder' ? 'Backorder' : 'Out of Stock';
		stock.style.cssText = 'font-size: 0.75rem; font-weight: 600; color: ' + stockColor + '; padding: 2px 8px; border-radius: 12px; background: rgba(' + (stockStatus === 'in_stock' ? '40,167,69' : stockStatus === 'backorder' ? '255,193,7' : '220,53,69') + ', 0.1);';
		meta.appendChild(stock);
	}

	content.appendChild(meta);
	item.appendChild(content);

	// Hover effect
	item.addEventListener('mouseenter', function() {
		this.style.backgroundColor = '#f8f9fa';
		this.style.borderColor = '#d9272e';
		this.style.boxShadow = '0 2px 8px rgba(230, 126, 34, 0.2)';
	});
	item.addEventListener('mouseleave', function() {
		this.style.backgroundColor = '#ffffff';
		this.style.borderColor = '#e9ecef';
		this.style.boxShadow = 'none';
	});

	item.addEventListener('click', function() {
		_selectSuggestion(suggestion);
	});

	return item;
}

function _createSection(title, items, type) {
	var section = document.createElement('div');

	var header = document.createElement('div');
	header.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e9ecef;';

	var icon = '';
	var color = '#1e3a5f';
	if (type === 'department') {
		icon = '<svg style="width: 16px; height: 16px; color: ' + color + ';" fill="currentColor" viewBox="0 0 20 20"><path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" /></svg>';
	} else if (type === 'category') {
		icon = '<svg style="width: 16px; height: 16px; color: ' + color + ';" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" /></svg>';
	} else if (type === 'code') {
		icon = '<svg style="width: 16px; height: 16px; color: ' + color + ';" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" /></svg>';
	} else {
		icon = '<svg style="width: 16px; height: 16px; color: ' + color + ';" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" /><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" /></svg>';
	}

	header.innerHTML = icon + '<span style="font-weight: 700; font-size: 13px; color: ' + color + '; text-transform: uppercase; letter-spacing: 0.5px;">' + title + '</span>';
	section.appendChild(header);

	var list = document.createElement('div');
	list.style.cssText = 'display: flex; flex-direction: column; gap: 6px;';

	items.forEach(function(suggestion, index) {
		var item = document.createElement('div');
		item.className = 'suggestion-item';
		item.style.cssText = 'padding: 10px 12px; cursor: pointer; border-radius: 6px; transition: all 0.2s; background: #ffffff; border: 1px solid transparent;';
		item.setAttribute('data-index', index);

		var display = document.createElement('div');
		display.textContent = suggestion.display;
		display.style.cssText = 'font-weight: 500; font-size: 13px; color: #212529;';

		if (suggestion.departmentName) {
			var dept = document.createElement('div');
			dept.textContent = suggestion.departmentName;
			dept.style.cssText = 'font-size: 0.75rem; color: #6c757d; margin-top: 2px;';
			item.appendChild(display);
			item.appendChild(dept);
			} else {
			item.appendChild(display);
		}

		item.addEventListener('mouseenter', function() {
			this.style.backgroundColor = '#f8f9fa';
			this.style.borderColor = '#d9272e';
		});
		item.addEventListener('mouseleave', function() {
			this.style.backgroundColor = '#ffffff';
			this.style.borderColor = 'transparent';
		});

		item.addEventListener('click', function() {
			_selectSuggestion(suggestion);
		});

		list.appendChild(item);
	});

	section.appendChild(list);
	return section;
}

function _sendMessageToIframe(message) {
	if (_iframe && _iframe.contentWindow) {
		var origin = new URL(_appBaseUrl).origin;
		_iframe.contentWindow.postMessage(message, origin);
	}
}

function _handleMessageFromIframe(event) {
	// Verify message origin
	var expectedOrigin = new URL(_appBaseUrl).origin;
	if (event.origin !== expectedOrigin) {
		return;
	}

	var message = event.data;

	if (!message || !message.type) {
		return;
	}

	switch (message.type) {
		case 'IFRAME_READY':
			// Iframe is ready, we can start requesting suggestions
			__pfDbg('PartsSearch: Iframe ready');
			break;
		case 'SUGGESTIONS_RESPONSE':
			_isLoadingSuggestions = false;
			_suggestionsQuery = _lastRequestedQuery;
			// Received suggestions from iframe
			if (message.data && message.data.suggestions) {
				_renderSuggestions(message.data.suggestions);
			}
			// A pending Enter was waiting on exactly this query → apply it now
			// (detected vehicle if any, else plain search).
			if (_pendingEnterQuery !== null && _pendingEnterQuery === _suggestionsQuery &&
			    _pendingEnterQuery === (_currentQuery || '').trim()) {
				_pendingEnterQuery = null;
				_navigateWithDetectedFilters(_currentQuery);
			}
			break;
		case 'SUGGESTIONS_ERROR':
			_isLoadingSuggestions = false;
			_suggestionsQuery = _lastRequestedQuery;
			// Error getting suggestions
			console.warn('PartsSearch: Error getting suggestions:', message.data?.error);
			// If an Enter was waiting on this query, don't leave it hanging — fall
			// back to a plain search rather than doing nothing.
			if (_pendingEnterQuery !== null && _pendingEnterQuery === _suggestionsQuery) {
				var pq = _pendingEnterQuery;
				_pendingEnterQuery = null;
				_navigateToPartsFinder(pq);
			} else {
				_hideSuggestions();
			}
			break;
		case 'ASSET_URLS_RESPONSE':
		case 'ASSET_URLS_ERROR':
			// These are for the embed script, ignore them silently
			break;
		case 'SLIDER_PARTS_RESPONSE':
		case 'SLIDER_PARTS_ERROR':
			// These are for the slider script, ignore them silently
			break;
		default:
			// Only warn for truly unknown types (not from other scripts)
			if (message.type && !message.type.startsWith('ASSET_') && !message.type.startsWith('SLIDER_')) {
				console.warn('PartsSearch: Unknown message type from iframe', message.type);
			}
	}
}

export function init() {
	// Resolve global config from the shared bundle (NOT document.currentScript —
	// there is only one storefront <script> tag now, and it isn't this widget's).
	_appBaseUrl = getAppBaseUrl();

	// Parent site base URL (the storefront this bundle is injected into).
	_parentSiteBaseUrl = window.location.origin;
	if (!_parentSiteBaseUrl.endsWith('/')) {
		_parentSiteBaseUrl += '/';
	}

	// Defer search-box detection so the storefront's own header/menu scripts have
	// settled (they enable/replace the search input late) — preserves the legacy
	// 500ms defer. _initializeSearch no-ops cleanly if no search input is present,
	// so init() is safe to call on any page (global widget, no dedicated container).
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			setTimeout(_initializeSearch, 500);
		}, { once: true });
	} else {
		setTimeout(_initializeSearch, 500);
	}
}

export var id = 'parts-search';
